import { useEffect, useMemo, useState } from "react";
import "./index.css";
import { content } from "./content/translations";
import ProductScreens from "./components/ProductScreens";

function getInitialLang() {
  const stored = localStorage.getItem("opement_lang");
  return stored === "tr" ? "tr" : "en";
}

export default function App() {
  const [lang, setLang] = useState(getInitialLang);
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);
  const [formError, setFormError] = useState("");
  const [requestType, setRequestType] = useState("quote");
  const t = useMemo(() => content[lang], [lang]);

  useEffect(() => {
    localStorage.setItem("opement_lang", lang);
    document.documentElement.lang = lang;
  }, [lang]);

  const setLanguage = (value) => setLang(value);

  const submitDemo = async (event) => {
    event.preventDefault();

    const form = event.currentTarget;
    const data = new FormData(form);

    setSending(true);
    setFormError("");

    try {
      const response = await fetch("/api/website/leads", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          request_type: requestType,
          full_name: data.get("full_name"),
          company: data.get("company"),
          email: data.get("email"),
          phone: data.get("phone"),
          industry: data.get("industry"),
          message: data.get("message"),
          language: lang,
          source_url: window.location.href,
          website: data.get("website")
        })
      });

      const result = await response.json().catch(() => ({}));

      if (!response.ok) {
        throw new Error(
          result.detail ||
          t.demo.error
        );
      }

      setSent(true);
      form.reset();

    } catch (error) {
      setFormError(
        error?.message ||
        t.demo.error
      );

    } finally {
      setSending(false);
    }
  };

  return (
    <div className="site" id="top">
      <header className="navbar">
        <a className="brand" href="#top" aria-label="Opement home">
          <span className="brand-mark"><img src="/logo-icon-clean.png" alt="" /></span>
          <span className="brand-name">OPEMENT</span>
        </a>
        <nav className="nav-links" aria-label="Main navigation">
          <a href="#platform">{t.nav.platform}</a>
          <a href="#modules">{t.nav.modules}</a>
          <a href="#packages">{t.nav.packages}</a>
          <a href="#industries">{t.nav.industries}</a>
          <a href="#intelligence">{t.nav.intelligence}</a>
        </nav>
        <div className="nav-actions">
          <div className="language" aria-label="Language selector">
            <button className={lang === "en" ? "active" : ""} onClick={() => setLanguage("en")}>EN</button>
            <button className={lang === "tr" ? "active" : ""} onClick={() => setLanguage("tr")}>TR</button>
          </div>
          <a
            className="login"
            href="https://demo.opement.com/login"
            target="_blank"
            rel="noopener noreferrer"
          >
            {t.nav.login}
          </a>
          <a className="nav-demo" href="#demo">{t.nav.demo}</a>
        </div>
      </header>

      <main>
        <section className="hero" id="platform">
          <div className="hero-media" aria-hidden="true">
          </div>
          <div className="hero-shade" aria-hidden="true" />
          <div className="hero-content">
            <div className="hero-logo-line">
              <img src="/logo-icon-clean.png" alt="" />
              <span>{t.footer}</span>
            </div>
            <p className="eyebrow light">{t.hero.eyebrow}</p>
            <h1>{t.hero.title}</h1>
            <p className="hero-text">{t.hero.text}</p>
            <p className="hero-note">{t.hero.note}</p>
            <div className="hero-actions">
              <a className="button primary" href="#demo">{t.hero.primary}</a>
              <a className="button ghost" href="#modules">{t.hero.secondary}</a>
            </div>
          </div>
        </section>

        <section className="value-strip" aria-label="Opement value highlights">
          {t.valueCards.map((card) => (
            <article key={card.title}>
              <span className="card-icon" />
              <h3>{card.title}</h3>
              <p>{card.text}</p>
            </article>
          ))}
        </section>

        {t.productScreens && (
          <ProductScreens data={t.productScreens} />
        )}

        <section className="section modules" id="modules">
          <div className="section-head compact">
            <p className="eyebrow">{t.modules.eyebrow}</p>
            <h2>{t.modules.title}</h2>
            <p>{t.modules.text}</p>
          </div>
          <div className="module-grid">
            {t.modules.groups.map((group) => (
              <article className="module-card" key={group.title}>
                <div className="module-icon" />
                <h3>{group.title}</h3>
                <ul>
                  {group.items.map((item) => <li key={item}>{item}</li>)}
                </ul>
              </article>
            ))}
          </div>
        </section>

        <section className="section packages" id="packages">
          <div className="section-head center packages-head">
            <p className="eyebrow">{t.packages.eyebrow}</p>
            <h2>{t.packages.title}</h2>
            <p>{t.packages.text}</p>
          </div>

          <div className="package-grid">
            {t.packages.items.map((plan) => (
              <article
                className={`package-card ${plan.featured ? "featured" : ""}`}
                key={plan.code}
              >
                {plan.featured && (
                  <span className="package-badge">
                    {plan.name}
                  </span>
                )}

                <div className="package-card-head">
                  <h3>{plan.name}</h3>
                  <strong>{plan.subtitle}</strong>
                  <p>{plan.description}</p>
                </div>

                <ul className="package-features">
                  {plan.features.map((feature) => (
                    <li key={feature}>{feature}</li>
                  ))}
                </ul>

                <a
                  className="package-cta"
                  href="#demo"
                  onClick={() => setRequestType("quote")}
                >
                  {t.packages.cta}
                </a>
              </article>
            ))}
          </div>

          <p className="package-pricing-note">
            {t.packages.noPrice}
          </p>
        </section>

        <section className="section intelligence" id="intelligence">
          <div className="intelligence-copy">
            <p className="eyebrow">{t.intelligence.eyebrow}</p>
            <h2>{t.intelligence.title}</h2>
            <p>{t.intelligence.text}</p>
          </div>
          <div className="process-list">
            {t.intelligence.steps.map((step) => (
              <article key={step.n}>
                <span>{step.n}</span>
                <div>
                  <h3>{step.title}</h3>
                  <p>{step.text}</p>
                </div>
              </article>
            ))}
          </div>
        </section>

        <section className="section industries" id="industries">
          <div className="section-head center">
            <p className="eyebrow">{t.industries.eyebrow}</p>
            <h2>{t.industries.title}</h2>
          </div>
          <div className="industry-tags">
            {t.industries.list.map((item) => <span key={item}>{item}</span>)}
          </div>
          <div className="industry-visuals" aria-hidden="true">
            <div className="visual visual-one" />
            <div className="visual visual-two" />
            <div className="visual visual-three" />
          </div>
        </section>

        <section className="section demo" id="demo">
          <div className="demo-copy">
            <p className="eyebrow">{t.demo.eyebrow}</p>
            <h2>{t.demo.title}</h2>
            <p>{t.demo.text}</p>
          </div>
          <form className="demo-form" onSubmit={submitDemo}>
            {sent ? (
              <div className="success">{t.demo.success}</div>
            ) : (
              <>
                <div className="request-type-switch">
                  <button
                    type="button"
                    className={requestType === "quote" ? "active" : ""}
                    onClick={() => setRequestType("quote")}
                  >
                    {t.demo.quoteOption}
                  </button>

                  <button
                    type="button"
                    className={requestType === "demo" ? "active" : ""}
                    onClick={() => setRequestType("demo")}
                  >
                    {t.demo.demoOption}
                  </button>
                </div>

                <div className="form-row">
                  <input
                    required
                    name="full_name"
                    placeholder={t.demo.placeholders[0]}
                  />

                  <input
                    required
                    name="company"
                    placeholder={t.demo.placeholders[1]}
                  />
                </div>

                <div className="form-row">
                  <input
                    required
                    type="email"
                    name="email"
                    placeholder={t.demo.placeholders[2]}
                  />

                  <input
                    name="phone"
                    placeholder={t.demo.placeholders[3]}
                  />
                </div>

                <input
                  name="industry"
                  placeholder={t.demo.placeholders[4]}
                />

                <textarea
                  name="message"
                  rows="5"
                  placeholder={t.demo.placeholders[5]}
                />

                <input
                  name="website"
                  tabIndex="-1"
                  autoComplete="off"
                  aria-hidden="true"
                  style={{
                    position: "absolute",
                    left: "-9999px",
                    opacity: 0
                  }}
                />

                {formError && (
                  <div className="form-error">
                    {formError}
                  </div>
                )}

                <button
                  className="button primary submit"
                  type="submit"
                  disabled={sending}
                >
                  {sending
                    ? t.demo.sending
                    : requestType === "quote"
                      ? t.demo.submitQuote
                      : t.demo.submitDemo}
                </button>
              </>
            )}
          </form>
        </section>
      </main>

      <footer className="footer">
        <a className="footer-brand" href="#top"><img src="/logo-icon-clean.png" alt="" /><span>OPEMENT</span></a>
        <p>{t.footer}</p>
        <span className="footer-note">Industrial Intelligence Platform</span>
      </footer>
    </div>
  );
}
